import { useMemo } from 'react'
import { calculateTradeIntelligence, type TradeIntelligenceResult } from '../lib/trade-intelligence'
import { useSettingsStore } from '../stores/settingsStore'
import { IndexSymbol, type AnalysisMode, type AnalysisResult, type MarketData } from '../types'
import { useAnalysisHistory } from './useAnalysisHistory'
import { useDashboardData } from './useDashboardData'
import { useRsiSeries } from './useRsiSeries'

interface UseTradeSignalsProps {
    symbol: string
    expiry?: string
    mode: AnalysisMode
    inspectionSource?: 'CurrentIntraday' | 'HistoricalSnapshot' // Default to CurrentIntraday

    // Inspector Specific Options
    selectedStrikes?: number[] // Specific strikes to analyze (affects history variance calc)
    rsiParams?: {
        selectedSingleStrike: number
        selectedCeStrike: number
        selectedPeStrike: number
        selectedCustomCeStrikes: number[]
        selectedCustomPeStrikes: number[]
        rsiTimeFrame?: number
    }
    // Date Range Options
    rangeStartDate?: string
    rangeEndDate?: string
    historicalDate?: string | null
}

interface UseTradeSignalsResult {
    signal: TradeIntelligenceResult | null
    activeAnalysis: (AnalysisResult & { variance: any }) | null
    scannerRsiData: { ceRsi: number; peRsi: number; rsiDiff: number } | undefined
    history: any[]
    marketData: MarketData | null
    isHistoryLoading: boolean
    rsiSeriesData: any[] // Expose full RSI series for Inspector charts
}

/**
 * Centralized Hook for Trade Signals
 * Ensures parity between Scanner and Inspector by reusing exact same data prep logic.
 */
export const useTradeSignals = ({
    symbol,
    expiry,
    mode,
    inspectionSource = 'CurrentIntraday',
    selectedStrikes = [],
    rsiParams,
    rangeStartDate,
    rangeEndDate,
    historicalDate,
}: UseTradeSignalsProps): UseTradeSignalsResult => {
    // 1. Get Live Data (Dashboard/Pulse)
    const { marketData, analysis, effectiveExpiry, isLoading: isLiveLoading } = useDashboardData()

    // 2. Get History (Unified Logic)
    const { history: rawHistory, data: analyzedHistory, isLoading: loadingLocalHistory } = useAnalysisHistory({
        symbol: symbol as IndexSymbol,
        expiry: effectiveExpiry || '',
        range: 20,
        mode: mode === 'VOLUME' ? 'VOLUME' : 'OI',
        baseTimePattern: undefined, // Scanner uses full intraday usually
    })

    const history = useMemo(() => rawHistory || [], [rawHistory])
    const latestSnapshot = analyzedHistory && analyzedHistory.length > 0 ? (analyzedHistory[analyzedHistory.length - 1] as any) : null

    // 3. Prepare Active Analysis (Priority: Live > History)
    const activeAnalysis = useMemo(() => {
        // history items from useAnalysisHistory are already flat AnalysisResults
        const base = inspectionSource === 'CurrentIntraday' ? analysis || latestSnapshot : latestSnapshot || analysis

        if (!base) return null

        const raw = base as any

        // Robust Adapter to ensure TradeIntel gets nested variance structure
        return {
            ...raw,
            variance: {
                ...(raw.variance || {}),
                totalCount: raw.totalCount ?? raw.variance?.totalCount ?? 0,
                ceCount: raw.ceCount ?? raw.variance?.ceCount ?? 0,
                peCount: raw.peCount ?? raw.variance?.peCount ?? 0,
                finalVariance:
                    raw.variance?.finalVariance ??
                    raw.variance?.variance ??
                    raw.oiVariance ?? // Patch for MarketMetrics flat structure
                    0,
            },
        }
    }, [analysis, latestSnapshot, inspectionSource])

    // 4. Prepare RSI Data (Inspector Logic with useRsiSeries)
    // CRITICAL FIX: Use S1/R1 strikes from analysis (like Inspector), NOT ATM strike
    // This ensures Scanner calculates RSI from the same strikes as Inspector
    const rsiStrikeCe = useMemo(() => {
        if (rsiParams?.selectedCeStrike) return rsiParams.selectedCeStrike
        // Fallback to R1 (Resistance) strike from analysis - this is what Inspector uses
        return activeAnalysis?.resistanceStrike || 0
    }, [activeAnalysis, rsiParams])

    const rsiStrikePe = useMemo(() => {
        if (rsiParams?.selectedPeStrike) return rsiParams.selectedPeStrike
        // Fallback to S1 (Support) strike from analysis - this is what Inspector uses
        return activeAnalysis?.supportStrike || 0
    }, [activeAnalysis, rsiParams])

    const rsiSeriesData = useRsiSeries({
        localHistory: history || [],
        // CRITICAL: Use "Multiple" mode (like Inspector) to get separate CE/PE RSI from S1/R1 strikes
        mode: rsiParams ? 'Custom' : 'Multiple',
        // These are NOT used in Multiple mode, but keep for Custom mode compatibility
        selectedSingleStrike: rsiParams?.selectedSingleStrike || 0,
        // CRITICAL: Pass R1 for CE, S1 for PE (like Inspector auto-selects)
        selectedCeStrike: rsiStrikeCe,
        selectedPeStrike: rsiStrikePe,
        selectedCustomCeStrikes: rsiParams?.selectedCustomCeStrikes || [],
        selectedCustomPeStrikes: rsiParams?.selectedCustomPeStrikes || [],
        symbol: symbol,
        expiry: effectiveExpiry,
        rsiTimeFrame: rsiParams?.rsiTimeFrame || 14,
    })

    // DEBUG: Log RSI flow to diagnose Scanner vs Inspector discrepancy
    // RSI Debug info - silent
    /*
    console.log( '[useTradeSignals] RSI Debug:', {
        historyLength: history?.length || 0,
        rsiStrikeCe,
        rsiStrikePe,
        rsiSeriesLength: rsiSeriesData?.length || 0,
        rsiSeriesNames: rsiSeriesData?.map( ( s ) => s.name ) || [],
        latestSnapshotHasRsi: !!latestSnapshot?.rsi,
        analysisR1: activeAnalysis?.resistanceStrike,
        analysisS1: activeAnalysis?.supportStrike,
    } )
    */

    const scannerRsiData = useMemo(() => {
        // 1. Try extracting from rsiSeriesData first (most accurate)
        if (rsiSeriesData && rsiSeriesData.length > 0) {
            // Use .includes() for more flexible matching (same as Inspector)
            const ceSeries = rsiSeriesData.find((s) => s.name.includes('CE'))?.data || []
            const peSeries = rsiSeriesData.find((s) => s.name.includes('PE'))?.data || []

            const ceRsi = ceSeries.length > 0 ? (ceSeries[ceSeries.length - 1]?.value ?? null) : null
            const peRsi = peSeries.length > 0 ? (peSeries[peSeries.length - 1]?.value ?? null) : null

            if (ceRsi !== null && peRsi !== null) {
                return { ceRsi, peRsi, rsiDiff: ceRsi - peRsi }
            }
        }

        // 2. Fallback: Try latestSnapshot.rsi
        if (latestSnapshot?.rsi) {
            return latestSnapshot.rsi
        }

        // 3. Fallback: Try to find RSI in ANY history entry (search backwards)
        if (history && history.length > 0) {
            for (let i = history.length - 1; i >= 0; i--) {
                const snap = history[i] as any
                if (snap?.rsi?.ceRsi !== undefined && snap?.rsi?.peRsi !== undefined) {
                    return snap.rsi
                }
            }
        }

        // 4. Ultimate fallback: Return undefined (let trade-intelligence handle it)
        // Don't return 50/50 as that would mask the issue
        return undefined
    }, [rsiSeriesData, latestSnapshot, history])

    // 5. Calculate Signal
    const signal = useMemo(() => {
        if (!activeAnalysis || !marketData) return null

        return calculateTradeIntelligence({
            marketData,
            analysis: activeAnalysis,
            mode,
            history: history as any[],
            rsiData: scannerRsiData,
        })
    }, [marketData, activeAnalysis, mode, history, scannerRsiData])

    return {
        signal,
        activeAnalysis,
        scannerRsiData,
        history,
        marketData,
        isHistoryLoading: loadingLocalHistory || !history || history.length < 3,
        rsiSeriesData,
    }
}
